import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Ingredient, ingredientLabel } from '../utils/recipeFormat';

// Shape stored on Recipe.card -- see backend/recipes/recipe_card.py.
export interface RecipeCardStep {
  action: string;
  ingredients: number[]; // indexes into the recipe's ingredient list
  steps: number[]; // indexes of earlier steps that feed this one
}

export interface RecipeCardData {
  setup: string[];
  steps: RecipeCardStep[];
}

interface Cell {
  key: string;
  row: number; // first grid row (1-based), setup rows included
  rowSpan: number;
  col: number; // first grid column (1-based); column 1 is ingredients
  colSpan: number;
  kind: 'ingredient' | 'action' | 'empty';
  text?: string;
}

interface Layout {
  cells: Cell[];
  columns: number; // action columns, not counting the ingredient column
  rows: number;
}

// A stored card is shown only if it accounts for every ingredient exactly
// once as a single tree -- the backend already enforces this, but checking
// again here means the layout never has to guess around a bad card.
export const isCompleteCard = (card: RecipeCardData, ingredientCount: number): boolean => {
  const seenIngredients = new Set<number>();
  const usedSteps = new Set<number>();
  for (let i = 0; i < card.steps.length; i += 1) {
    const step = card.steps[i];
    if (!step.action || (!step.ingredients.length && !step.steps.length)) return false;
    for (const idx of step.ingredients) {
      if (idx < 0 || idx >= ingredientCount || seenIngredients.has(idx)) return false;
      seenIngredients.add(idx);
    }
    for (const s of step.steps) {
      if (s < 0 || s >= i || usedSteps.has(s)) return false;
      usedSteps.add(s);
    }
  }
  return seenIngredients.size === ingredientCount && card.steps.length - usedSteps.size === 1;
};

// Turns the step tree into grid cells. Walking the tree depth-first from
// each final step lays ingredients out so every action's inputs are adjacent
// rows; an action's column is one past the deepest thing feeding it, and
// "empty" cells fill the gap when an input sits further left than the
// column right before its action (e.g. eggs joining a later mix).
export const layoutCard = (card: RecipeCardData, ingredients: Ingredient[]): Layout => {
  const steps = card.steps;
  const setupRows = card.setup.length;
  const depth: number[] = [];
  const consumed = new Set<number>();
  steps.forEach((step, i) => {
    const childDepths = step.steps.filter((s) => s < i).map((s) => depth[s]);
    depth[i] = 1 + Math.max(0, ...childDepths);
    step.steps.forEach((s) => consumed.add(s));
  });
  const roots = steps.map((_, i) => i).filter((i) => !consumed.has(i));
  const columns = Math.max(0, ...roots.map((i) => depth[i]));

  const cells: Cell[] = [];
  const placedSteps = new Set<number>();
  let row = 0; // ingredient rows placed so far

  const gridRow = (r: number) => setupRows + r + 1;
  const fill = (key: string, start: number, end: number, fromDepth: number, toDepth: number) => {
    // Empty cell covering action columns fromDepth+1 .. toDepth-1.
    if (toDepth - fromDepth > 1 && end > start) {
      cells.push({
        key, kind: 'empty', row: gridRow(start), rowSpan: end - start,
        col: fromDepth + 2, colSpan: toDepth - fromDepth - 1,
      });
    }
  };
  const placeIngredient = (idx: number) => {
    const ing = ingredients[idx];
    cells.push({ key: `i${idx}`, kind: 'ingredient', row: gridRow(row), rowSpan: 1, col: 1, colSpan: 1, text: ingredientLabel(ing) });
    row += 1;
  };
  const placeStep = (i: number): [number, number] => {
    placedSteps.add(i);
    const start = row;
    steps[i].steps.forEach((s) => {
      if (s >= i || placedSteps.has(s)) return;
      const childStart = row;
      placeStep(s);
      fill(`f${s}`, childStart, row, depth[s], depth[i]);
    });
    steps[i].ingredients.forEach((idx) => {
      const r = row;
      placeIngredient(idx);
      fill(`fi${idx}`, r, row, 0, depth[i]);
    });
    if (row > start) {
      cells.push({
        key: `s${i}`, kind: 'action', row: gridRow(start), rowSpan: row - start,
        col: depth[i] + 1, colSpan: 1, text: steps[i].action,
      });
    }
    return [start, row];
  };

  // isCompleteCard guarantees a single final step; roots is that one step.
  roots.forEach((i) => {
    const [start, end] = placeStep(i);
    fill(`r${i}`, start, end, depth[i], columns + 1);
  });

  card.setup.forEach((text, i) => {
    cells.push({ key: `setup${i}`, kind: 'action', row: i + 1, rowSpan: 1, col: 1, colSpan: columns + 1, text });
  });

  return { cells, columns, rows: setupRows + row };
};

const CardGrid: React.FC<{ layout: Layout }> = ({ layout }) => (
  <div
    className="recipe-card-grid"
    style={{ gridTemplateColumns: `minmax(var(--card-ingredient-min), 3fr) repeat(${layout.columns}, minmax(var(--card-action-min), 1fr))` }}
  >
    {layout.cells.map((cell) => (
      <div
        key={cell.key}
        className={`recipe-card-cell recipe-card-${cell.kind}`}
        style={{
          gridRow: `${cell.row} / span ${cell.rowSpan}`,
          gridColumn: `${cell.col} / span ${cell.colSpan}`,
        }}
      >
        {cell.text}
      </div>
    ))}
  </div>
);

interface Props {
  name: string;
  card: RecipeCardData;
  ingredients: Ingredient[];
}

// Inline card that opens full screen on tap. Full screen also asks the
// browser to lock landscape where that's supported (Android Chrome); on
// iOS, which supports neither, the overlay still fills the viewport and the
// card reflows when the phone is turned sideways.
const RecipeCard: React.FC<Props> = ({ name, card, ingredients }) => {
  const layout = useMemo(() => layoutCard(card, ingredients), [card, ingredients]);
  const [fullScreen, setFullScreen] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setFullScreen(false);
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => undefined);
    }
    try {
      (window.screen.orientation as any)?.unlock?.();
    } catch {
      // Not supported -- nothing to undo.
    }
  }, []);

  useEffect(() => {
    if (!fullScreen) return undefined;
    const el = overlayRef.current;
    el?.requestFullscreen?.()
      .then(() => (window.screen.orientation as any)?.lock?.('landscape'))
      .catch(() => undefined);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    // Leaving browser full screen (back gesture, Esc) closes the overlay too.
    const onFullscreenChange = () => {
      if (!document.fullscreenElement) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, [fullScreen, close]);

  return (
    <>
      <button
        type="button"
        className="recipe-card-inline"
        onClick={() => setFullScreen(true)}
        aria-label={`Open the ${name} recipe card full screen`}
      >
        <CardGrid layout={layout} />
        <span className="recipe-card-hint">Tap to expand · turn your phone sideways for the best view</span>
      </button>

      {fullScreen && createPortal(
        <div ref={overlayRef} className="recipe-card-fullscreen" onClick={close} role="dialog" aria-label={`${name} recipe card`}>
          <button type="button" className="modal-close" onClick={close} aria-label="Close recipe card">✕</button>
          <div className="recipe-card-fullscreen-body" onClick={(e) => e.stopPropagation()}>
            <h2>{name}</h2>
            <CardGrid layout={layout} />
            <p className="recipe-card-rotate-hint">Turn your phone sideways for a bigger card</p>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default RecipeCard;
